import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "./ToastProvider";
import "../styles/Modal.css";

const EMPTY_CAMPAIGN_VALUE = "__none__";
const EMPTY_DOMAIN_VALUE = "__default__";

function getDefaultDomain() {
  const base = process.env.REACT_APP_BASE_URL || "https://sqir.link";
  try {
    return new URL(base).hostname;
  } catch {
    return base.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  }
}

function toIsoOrEmpty(localDateTime) {
  if (!localDateTime) return "";
  const parsed = new Date(localDateTime);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString();
}

export default function BulkEditModal({ campaigns, selectedCount, loading, onClose, onSubmit }) {
  const { addToast } = useToast();
  const defaultDomain = getDefaultDomain();

  const [verifiedDomains, setVerifiedDomains] = useState([]);
  const [domainLoading, setDomainLoading] = useState(false);

  const [applyCampaign, setApplyCampaign] = useState(false);
  const [campaignId, setCampaignId] = useState(EMPTY_CAMPAIGN_VALUE);

  const [applyTags, setApplyTags] = useState(false);
  const [tags, setTags] = useState("");

  const [applyDomain, setApplyDomain] = useState(false);
  const [domain, setDomain] = useState(EMPTY_DOMAIN_VALUE);

  const [applyMobileUrl, setApplyMobileUrl] = useState(false);
  const [mobileUrl, setMobileUrl] = useState("");

  const [applyDates, setApplyDates] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  const [applyRedirect, setApplyRedirect] = useState(false);
  const [redirectType, setRedirectType] = useState("302");
  const [redirectTime, setRedirectTime] = useState(10);

  const [passwordAction, setPasswordAction] = useState("no_change");
  const [password, setPassword] = useState("");

  const [applyUtm, setApplyUtm] = useState(false);
  const [clearExistingUtm, setClearExistingUtm] = useState(false);
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmTerm, setUtmTerm] = useState("");
  const [utmContent, setUtmContent] = useState("");

  const [error, setError] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadDomains() {
      setDomainLoading(true);
      try {
        const data = await api.getDomains();
        const domainsList = data.domains || data || [];
        if (!cancelled && Array.isArray(domainsList)) {
          const activeDomains = domainsList
            .filter((domainDoc) => domainDoc.status === "active")
            .map((domainDoc) => domainDoc.domain)
            .filter(Boolean);
          setVerifiedDomains([...new Set(activeDomains)]);
        }
      } catch (err) {
        if (!cancelled) {
          addToast("Failed to load verified domains.", "warning");
        }
      } finally {
        if (!cancelled) setDomainLoading(false);
      }
    }

    loadDomains();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  const domainOptions = useMemo(() => [defaultDomain, ...verifiedDomains.filter((domain) => domain !== defaultDomain)], [defaultDomain, verifiedDomains]);

  const updateCount = useMemo(() => {
    let count = 0;
    if (applyCampaign) count += 1;
    if (applyTags) count += 1;
    if (applyDomain) count += 1;
    if (applyMobileUrl) count += 1;
    if (applyDates) count += 1;
    if (applyRedirect) count += 1;
    if (passwordAction !== "no_change") count += 1;
    if (applyUtm) count += 1;
    return count;
  }, [applyCampaign, applyTags, applyDomain, applyMobileUrl, applyDates, applyRedirect, passwordAction, applyUtm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    const updates = {};

    if (applyCampaign) {
      updates.campaign_id = campaignId === EMPTY_CAMPAIGN_VALUE ? "" : campaignId;
    }

    if (applyTags) {
      updates.tags = tags;
    }

    if (applyDomain) {
      updates.domain = domain === EMPTY_DOMAIN_VALUE ? "" : domain.trim();
    }

    if (applyMobileUrl) {
      updates.mobile_url = mobileUrl.trim();
    }

    if (applyDates) {
      updates.expires_at = toIsoOrEmpty(expiresAt);
      updates.scheduled_at = toIsoOrEmpty(scheduledAt);
    }

    if (applyRedirect) {
      updates.redirect_type = redirectType;
      updates.redirect_time = Number(redirectTime);
    }

    if (passwordAction === "set") {
      if (!password.trim()) {
        setError("Enter a password or choose 'No change'.");
        return;
      }
      updates.password = password.trim();
    } else if (passwordAction === "remove") {
      updates.password = "";
    }

    if (applyUtm) {
      updates.utm = {
        clearExisting: clearExistingUtm,
        source: utmSource.trim(),
        medium: utmMedium.trim(),
        campaign: utmCampaign.trim(),
        term: utmTerm.trim(),
        content: utmContent.trim(),
      };
    }

    if (Object.keys(updates).length === 0) {
      setError("Pick at least one field to update.");
      return;
    }

    onSubmit(updates);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide bulk-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="bulk-edit-title-wrap">
            <div className="bulk-edit-title-row">
              <h2>Bulk Edit Links</h2>
              <span className="bulk-edit-summary-badge">
                <FiCheck size={12} /> {selectedCount} selected
              </span>
            </div>
            <p className="bulk-edit-intro">
              Apply shared changes to multiple links at once. Leave a section off to keep those values unchanged.
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>
            <FiX size={16} />
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="modal-body bulk-edit-body">
            <div className="bulk-edit-grid">
              <section className="bulk-edit-card">
                <div className="bulk-edit-card-header">
                  <div>
                    <h3>Campaign & Tags</h3>
                    <p>Group selected links or retag them in one pass.</p>
                  </div>
                  <label className="bulk-edit-toggle">
                    <input type="checkbox" checked={applyCampaign} onChange={(e) => setApplyCampaign(e.target.checked)} />
                    <span />
                  </label>
                </div>
                {applyCampaign && (
                  <div className="bulk-edit-card-body">
                    <div className="form-group">
                      <label>Campaign</label>
                      <select className="form-select" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
                        <option value={EMPTY_CAMPAIGN_VALUE}>Remove campaign</option>
                        {campaigns.map((campaign) => (
                          <option key={campaign.$id} value={campaign.$id}>{campaign.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="bulk-edit-card-divider" />
                <div className="bulk-edit-card-body">
                  <label className="checkbox-label bulk-edit-inline-check">
                    <input type="checkbox" checked={applyTags} onChange={(e) => setApplyTags(e.target.checked)} />
                    Update tags
                  </label>
                  {applyTags && (
                    <div className="form-group">
                      <input
                        type="text"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        placeholder="tag-one, tag-two"
                      />
                      <span className="form-hint">Comma-separated tags. Leave empty to clear.</span>
                    </div>
                  )}
                  </div>
              </section>

              <section className="bulk-edit-card">
                <div className="bulk-edit-card-header">
                  <div>
                    <h3>Destination Settings</h3>
                    <p>Move links between verified domains and mobile targets.</p>
                  </div>
                  <label className="bulk-edit-toggle">
                    <input type="checkbox" checked={applyDomain} onChange={(e) => setApplyDomain(e.target.checked)} />
                    <span />
                  </label>
                </div>
                {applyDomain && (
                  <div className="bulk-edit-card-body">
                    <div className="form-group">
                      <label>Custom domain</label>
                      <select
                        className="form-select"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        disabled={domainLoading}
                      >
                        <option value={EMPTY_DOMAIN_VALUE}>{defaultDomain} (default domain)</option>
                        {domainOptions.filter((verifiedDomain) => verifiedDomain !== defaultDomain).map((verifiedDomain) => (
                          <option key={verifiedDomain} value={verifiedDomain}>{verifiedDomain}</option>
                        ))}
                      </select>
                      <span className="form-hint">
                        {domainLoading ? "Loading verified domains..." : "Only verified domains are shown here."}
                      </span>
                    </div>
                    <div className="bulk-edit-card-divider" />
                    <label className="checkbox-label bulk-edit-inline-check">
                      <input type="checkbox" checked={applyMobileUrl} onChange={(e) => setApplyMobileUrl(e.target.checked)} />
                      Update mobile URL
                    </label>
                    {applyMobileUrl && (
                      <div className="form-group">
                        <input
                          type="url"
                          value={mobileUrl}
                          onChange={(e) => setMobileUrl(e.target.value)}
                          placeholder="https://m.example.com/page"
                        />
                        <span className="form-hint">Leave empty to clear the mobile-specific destination.</span>
                      </div>
                    )}
                  </div>
                )}
              </section>

              <section className="bulk-edit-card">
                <div className="bulk-edit-card-header">
                  <div>
                    <h3>Timing & Redirect</h3>
                    <p>Set when links activate, expire, or how they redirect.</p>
                  </div>
                  <label className="bulk-edit-toggle">
                    <input type="checkbox" checked={applyDates} onChange={(e) => setApplyDates(e.target.checked)} />
                    <span />
                  </label>
                </div>
                {applyDates && (
                  <div className="bulk-edit-card-body bulk-edit-two-col">
                    <div className="form-group">
                      <label>Expires at</label>
                      <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                      <span className="form-hint">Leave empty to clear expiration.</span>
                    </div>
                    <div className="form-group">
                      <label>Scheduled activation</label>
                      <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                      <span className="form-hint">Leave empty to keep links active immediately.</span>
                    </div>
                    <div className="form-group">
                      <label>Redirect type</label>
                      <select className="form-select" value={redirectType} onChange={(e) => setRedirectType(e.target.value)}>
                        <option value="302">302 - Temporary</option>
                        <option value="301">301 - Permanent</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Preview time</label>
                      <input
                        type="number"
                        min={0}
                        max={30}
                        value={redirectTime}
                        onChange={(e) => setRedirectTime(e.target.value)}
                        placeholder="0-30 seconds"
                      />
                    </div>
                  </div>
                )}
              </section>

              <section className="bulk-edit-card">
                <div className="bulk-edit-card-header">
                  <div>
                    <h3>Protection & Tracking</h3>
                    <p>Bulk manage passwords and campaign UTM parameters.</p>
                  </div>
                  <label className="bulk-edit-toggle">
                    <input type="checkbox" checked={applyRedirect || passwordAction !== "no_change" || applyUtm} onChange={(e) => {
                      if (!e.target.checked) {
                        setApplyRedirect(false);
                        setPasswordAction("no_change");
                        setApplyUtm(false);
                      } else {
                        setApplyRedirect(true);
                        setApplyUtm(true);
                      }
                    }} />
                    <span />
                  </label>
                </div>
                <div className="bulk-edit-card-body bulk-edit-triple-stack">
                  <div className="form-group">
                    <label>Password</label>
                    <select className="form-select" value={passwordAction} onChange={(e) => setPasswordAction(e.target.value)}>
                      <option value="no_change">No change</option>
                      <option value="set">Set password</option>
                      <option value="remove">Remove password</option>
                    </select>
                    {passwordAction === "set" && (
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                      />
                    )}
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label bulk-edit-inline-check">
                      <input type="checkbox" checked={applyUtm} onChange={(e) => setApplyUtm(e.target.checked)} />
                      Update UTM parameters
                    </label>
                    {applyUtm && (
                      <>
                        <label className="checkbox-label bulk-edit-inline-subcheck">
                          <input
                            type="checkbox"
                            checked={clearExistingUtm}
                            onChange={(e) => setClearExistingUtm(e.target.checked)}
                          />
                          Remove existing UTM params before applying values below
                        </label>
                        <div className="bulk-edit-two-col">
                          <input type="text" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="utm_source" />
                          <input type="text" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="utm_medium" />
                          <input type="text" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="utm_campaign" />
                          <input type="text" value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} placeholder="utm_term" />
                          <input type="text" value={utmContent} onChange={(e) => setUtmContent(e.target.value)} placeholder="utm_content" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </section>
            </div>

            {error && <div className="bulk-edit-error">{error}</div>}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading || updateCount === 0}>
              {loading ? "Applying..." : `Update ${selectedCount} link${selectedCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
